import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
import { formatTime } from '../timeline/timelineGeometry';
import styles from './Transport.module.scss';

interface TransportProps {
  showSafe: boolean;
  onToggleSafe?: () => void;
  /** Full screen and phone: the frame width. */
  wide?: boolean;
  /** The chip at the right ("Live preview · no credits"; the phone says "No credits to watch"). */
  liveLabel?: string;
}

/** The transport under the preview (design b1): back one bar, play, next bar, the time, the chips. */
const TransportComponent: React.FC<TransportProps> = ({ showSafe, onToggleSafe, wide = false, liveLabel }) => {
  const intl = useIntl();
  const { transport, doc } = useMusicVideoEditor();
  const isOn = transport.isPlaying || transport.isLoading;
  return (
    <div className={clsx(styles.transport, wide && styles.transportFull)}>
      <button
        type="button"
        className={styles.stepButton}
        aria-label={intl.formatMessage(editorMessages.backBar)}
        title={intl.formatMessage(editorMessages.backBar)}
        onClick={() => transport.stepBar(-1)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
          <path d="M18 6l-8 6 8 6z" />
          <path d="M6 6v12" />
        </svg>
      </button>
      <button
        type="button"
        className={styles.play}
        aria-label={intl.formatMessage(isOn ? editorMessages.pause : editorMessages.play)}
        disabled={!transport.canPlay}
        onClick={transport.toggle}
      >
        {isOn ? (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M7 5l12 7-12 7z" />
          </svg>
        )}
      </button>
      <button
        type="button"
        className={styles.stepButton}
        aria-label={intl.formatMessage(editorMessages.nextBar)}
        title={intl.formatMessage(editorMessages.nextBar)}
        onClick={() => transport.stepBar(1)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
          <path d="M6 6l8 6-8 6z" />
          <path d="M18 6v12" />
        </svg>
      </button>
      <span className={styles.timecode}>
        {formatTime(transport.positionSec, true)} <small>/ {formatTime(doc.settings.endSec, true)}</small>
      </span>
      <span className={styles.chips}>
        <span className={styles.live}>{liveLabel ?? intl.formatMessage(editorMessages.live)}</span>
        {onToggleSafe !== undefined && (
          <button
            type="button"
            className={clsx(styles.pill, showSafe && styles.pillOn)}
            aria-pressed={showSafe}
            onClick={onToggleSafe}
          >
            {intl.formatMessage(editorMessages.safeArea)}
          </button>
        )}
        {!wide && <span className={styles.pill}>{intl.formatMessage(editorMessages.ratio)}</span>}
      </span>
    </div>
  );
};

export const Transport = TypedMemo(TransportComponent);
